// ─────────────────────────────────────────────────────────────────────────────
// Full professional multi-business theme token system for the web app.
// Each business type gets a complete, high-contrast curated color palette.
// ─────────────────────────────────────────────────────────────────────────────

export interface BusinessThemeTokens {
  /** Primary brand color */
  primary: string;
  /** Darkened primary for hover, active states */
  primaryDark: string;
  /** Darkened primary (-10%) for deep shadows */
  primaryDarker: string;
  /** Lightened primary for highlights */
  primaryLight: string;
  /** Lightened primary (+10%) for subtle tones */
  primaryLighter: string;
  /** Very pale tint for section backgrounds & badges */
  primaryContainer: string;
  /** Complementary secondary color */
  secondary: string;
  /** Sidebar/nav dark background (high contrast slate/obsidian) */
  sidebarBg: string;
  /** Active nav item highlight */
  sidebarActive: string;
  /** Card border accent */
  cardBorder: string;
  /** Human-readable label */
  label: string;
}

export const BUSINESS_THEMES: Record<string, BusinessThemeTokens> = {
  // 💊 Pharmacy — Clinical Emerald Teal
  pharmacy: {
    label: 'Pharmacy',
    primary: '#0D9488',
    primaryDark: '#0F766E',
    primaryDarker: '#115E59',
    primaryLight: '#14B8A6',
    primaryLighter: '#5EEAD4',
    primaryContainer: '#CCFBF1',
    secondary: '#0284C7',
    sidebarBg: '#0F172A',
    sidebarActive: '#0F766E',
    cardBorder: '#99F6E4',
  },

  // 🍽️ Restaurant — Warm Amber
  restaurant: {
    label: 'Restaurant',
    primary: '#F97316',
    primaryDark: '#EA580C',
    primaryDarker: '#C2410C',
    primaryLight: '#FB923C',
    primaryLighter: '#FED7AA',
    primaryContainer: '#FFEDD5',
    secondary: '#92400E',
    sidebarBg: '#1C1917',
    sidebarActive: '#EA580C',
    cardBorder: '#FED7AA',
  },

  // 🍸 Bar / Nightclub — Premium Violet
  bar: {
    label: 'Bar',
    primary: '#8B5CF6',
    primaryDark: '#7C3AED',
    primaryDarker: '#5B21B6',
    primaryLight: '#A78BFA',
    primaryLighter: '#DDD6FE',
    primaryContainer: '#EDE9FE',
    secondary: '#5B21B6',
    sidebarBg: '#1E1B4B',
    sidebarActive: '#7C3AED',
    cardBorder: '#DDD6FE',
  },

  // ☕ Café — Warm Honey
  cafe: {
    label: 'Café',
    primary: '#D97706',
    primaryDark: '#B45309',
    primaryDarker: '#92400E',
    primaryLight: '#F59E0B',
    primaryLighter: '#FDE68A',
    primaryContainer: '#FEF3C7',
    secondary: '#78350F',
    sidebarBg: '#1C1917',
    sidebarActive: '#B45309',
    cardBorder: '#FDE68A',
  },

  // 🏥 Hospital / Clinic — Clinical Sky Blue
  hospital: {
    label: 'Hospital',
    primary: '#0284C7',
    primaryDark: '#0369A1',
    primaryDarker: '#075985',
    primaryLight: '#38BDF8',
    primaryLighter: '#BAE6FD',
    primaryContainer: '#E0F2FE',
    secondary: '#075985',
    sidebarBg: '#0F172A',
    sidebarActive: '#0369A1',
    cardBorder: '#BAE6FD',
  },

  // 🛒 Retail / General Store — Confident Royal Blue
  retail: {
    label: 'Retail',
    primary: '#2563EB',
    primaryDark: '#1D4ED8',
    primaryDarker: '#1E40AF',
    primaryLight: '#3B82F6',
    primaryLighter: '#BFDBFE',
    primaryContainer: '#DBEAFE',
    secondary: '#1E40AF',
    sidebarBg: '#0F172A',
    sidebarActive: '#1D4ED8',
    cardBorder: '#BFDBFE',
  },

  // 🥬 Grocery & Supermarket — Fresh Produce Emerald & Executive Dark Slate
  grocery: {
    label: 'Grocery',
    primary: '#10B981',
    primaryDark: '#059669',
    primaryDarker: '#047857',
    primaryLight: '#34D399',
    primaryLighter: '#A7F3D0',
    primaryContainer: '#ECFDF5',
    secondary: '#0F172A',
    sidebarBg: '#0F172A',
    sidebarActive: 'rgba(16, 185, 129, 0.16)',
    cardBorder: '#E2E8F0',
  },

  // 👗 Clothing / Boutique — Fashion Purple
  clothing: {
    label: 'Clothing',
    primary: '#9333EA',
    primaryDark: '#7E22CE',
    primaryDarker: '#6B21A8',
    primaryLight: '#A855F7',
    primaryLighter: '#E9D5FF',
    primaryContainer: '#F3E8FF',
    secondary: '#6B21A8',
    sidebarBg: '#2E1065',
    sidebarActive: '#7E22CE',
    cardBorder: '#E9D5FF',
  },

  // 👟 Shoe / Footwear — Footwear Amber Slate
  shoe: {
    label: 'Shoe Store',
    primary: '#D97706',
    primaryDark: '#B45309',
    primaryDarker: '#78350F',
    primaryLight: '#F59E0B',
    primaryLighter: '#FDE68A',
    primaryContainer: '#FEF3C7',
    secondary: '#B45309',
    sidebarBg: '#0F172A',
    sidebarActive: '#B45309',
    cardBorder: '#FDE68A',
  },

  // 🔨 Hardware — Industrial Steel & Gold
  hardware: {
    label: 'Hardware',
    primary: '#CA8A04',
    primaryDark: '#A16207',
    primaryDarker: '#713F12',
    primaryLight: '#EAB308',
    primaryLighter: '#FEF08A',
    primaryContainer: '#FEF9C3',
    secondary: '#A16207',
    sidebarBg: '#18181B',
    sidebarActive: '#A16207',
    cardBorder: '#FEF08A',
  },

  // 💎 Jewelry & Haute Horlogerie — Champagne Gold & Obsidian Noir Maison
  jewelry: {
    label: 'Jewelry & Watches',
    primary: '#C5A059',
    primaryDark: '#A8863E',
    primaryDarker: '#8C6D2D',
    primaryLight: '#D4AF37',
    primaryLighter: '#FDF3D8',
    primaryContainer: '#FEFDF8',
    secondary: '#D4AF37',
    sidebarBg: '#0B0F17',
    sidebarActive: '#1A2234',
    cardBorder: 'rgba(197, 160, 89, 0.28)',
  },

  // 🏨 Hotel & Hospitality PMS — Luxury Warm Amber & Deep Navy
  hotel: {
    label: 'Hotel & Hospitality',
    primary: '#D97706',
    primaryDark: '#B45309',
    primaryDarker: '#78350F',
    primaryLight: '#F59E0B',
    primaryLighter: '#FDE68A',
    primaryContainer: '#FFFBEB',
    secondary: '#1E293B',
    sidebarBg: '#0F172A',
    sidebarActive: '#B45309',
    cardBorder: '#FDE68A',
  },

  // 💇 Hair Salon & Beauty — Vibrant Rose
  salon: {
    label: 'Hair Salon & Beauty',
    primary: '#E11D48',
    primaryDark: '#BE123C',
    primaryDarker: '#9F1239',
    primaryLight: '#F43F5E',
    primaryLighter: '#FDA4AF',
    primaryContainer: '#FFE4E6',
    secondary: '#9D174D',
    sidebarBg: '#1C0F14',
    sidebarActive: '#BE123C',
    cardBorder: '#FECDD3',
  },

  // 💈 Barbershop — Rich Tobacco Amber & Slate
  barbershop: {
    label: 'Barbershop',
    primary: '#D97706',
    primaryDark: '#B45309',
    primaryDarker: '#78350F',
    primaryLight: '#F59E0B',
    primaryLighter: '#FDE68A',
    primaryContainer: '#FEF3C7',
    secondary: '#78350F',
    sidebarBg: '#1C1917',
    sidebarActive: '#B45309',
    cardBorder: '#FDE68A',
  },

  // 🏠 Real Estate & Property — Executive Trust Navy
  real_estate: {
    label: 'Real Estate',
    primary: '#1D4ED8',
    primaryDark: '#1E40AF',
    primaryDarker: '#1E3A8A',
    primaryLight: '#3B82F6',
    primaryLighter: '#93C5FD',
    primaryContainer: '#EFF6FF',
    secondary: '#0F172A',
    sidebarBg: '#0F172A',
    sidebarActive: '#1D4ED8',
    cardBorder: '#BFDBFE',
  },

  // 🧖 Spa & Wellness — Serene Sage & Sandalwood
  spa: {
    label: 'Spa & Wellness',
    primary: '#059669',
    primaryDark: '#047857',
    primaryDarker: '#064E3B',
    primaryLight: '#10B981',
    primaryLighter: '#A7F3D0',
    primaryContainer: '#ECFDF5',
    secondary: '#065F46',
    sidebarBg: '#05231B',
    sidebarActive: '#047857',
    cardBorder: '#A7F3D0',
  },

  // ✂️ Tailoring & Couture — Royal Artisan Violet
  tailoring: {
    label: 'Tailoring & Couture',
    primary: '#7C3AED',
    primaryDark: '#6D28D9',
    primaryDarker: '#5B21B6',
    primaryLight: '#8B5CF6',
    primaryLighter: '#C4B5FD',
    primaryContainer: '#EDE9FE',
    secondary: '#4C1D95',
    sidebarBg: '#1B1431',
    sidebarActive: '#6D28D9',
    cardBorder: '#DDD6FE',
  },

  // 🚗 Car Rental — Dynamic Ocean Drive Blue
  car_rental: {
    label: 'Car Rental',
    primary: '#0284C7',
    primaryDark: '#0369A1',
    primaryDarker: '#075985',
    primaryLight: '#38BDF8',
    primaryLighter: '#BAE6FD',
    primaryContainer: '#E0F2FE',
    secondary: '#0C4A6E',
    sidebarBg: '#0C1E33',
    sidebarActive: '#0284C7',
    cardBorder: '#BAE6FD',
  },

  // 📷 Photography Studio — Studio Noir & Gold
  photography: {
    label: 'Photography & Studio',
    primary: '#D97706',
    primaryDark: '#B45309',
    primaryDarker: '#92400E',
    primaryLight: '#F59E0B',
    primaryLighter: '#FDE68A',
    primaryContainer: '#FEF3C7',
    secondary: '#18181B',
    sidebarBg: '#18181B',
    sidebarActive: '#D97706',
    cardBorder: '#E4E4E7',
  },

  // 💼 Freelancer & Consulting — Modern Indigo Teal
  freelancer: {
    label: 'Professional Services',
    primary: '#0D9488',
    primaryDark: '#0F766E',
    primaryDarker: '#115E59',
    primaryLight: '#14B8A6',
    primaryLighter: '#5EEAD4',
    primaryContainer: '#CCFBF1',
    secondary: '#134E4A',
    sidebarBg: '#0D1E24',
    sidebarActive: '#0F766E',
    cardBorder: '#99F6E4',
  },

  // 🌿 Default — Fuko Deep Emerald
  default: {
    label: 'Default',
    primary: '#046A38',
    primaryDark: '#035C30',
    primaryDarker: '#024D28',
    primaryLight: '#057D42',
    primaryLighter: '#06904C',
    primaryContainer: '#D1FAE5',
    secondary: '#1B4D3E',
    sidebarBg: '#0B132B',
    sidebarActive: '#035C30',
    cardBorder: '#BBF7D0',
  },
};

/**
 * Resolves the full [BusinessThemeTokens] for a given business_type string or ID.
 */
export const getBusinessTheme = (businessType?: string | number, _isPureLite?: boolean): BusinessThemeTokens => {
  if (businessType === undefined || businessType === null) return BUSINESS_THEMES.default;
  if (businessType === 6 || businessType === '6') return BUSINESS_THEMES.jewelry;
  if (businessType === 217 || businessType === '217') return BUSINESS_THEMES.salon;
  if (businessType === 218 || businessType === '218') return BUSINESS_THEMES.barbershop;
  if (businessType === 219 || businessType === '219') return BUSINESS_THEMES.real_estate;
  if (businessType === 220 || businessType === '220') return BUSINESS_THEMES.spa;
  if (businessType === 221 || businessType === '221') return BUSINESS_THEMES.tailoring;
  if (businessType === 222 || businessType === '222') return BUSINESS_THEMES.car_rental;
  if (businessType === 227 || businessType === '227') return BUSINESS_THEMES.freelancer;
  const t = String(businessType).toLowerCase().trim();

  // 1. Real Estate & Property
  if (
    t.includes('real_estate') ||
    t.includes('realestate') ||
    t.includes('real estate') ||
    t.includes('property') ||
    t.includes('immobilier')
  ) {
    return BUSINESS_THEMES.real_estate;
  }

  // 2. Barbershop
  if (t.includes('barber') || t.includes('barbier')) return BUSINESS_THEMES.barbershop;

  // 3. Spa & Wellness
  if (t.includes('spa') && !t.includes('salon_spa')) return BUSINESS_THEMES.spa;
  if (t.includes('wellness') || t.includes('massage') || t.includes('sauna')) return BUSINESS_THEMES.spa;

  // 4. Hair Salon & Beauty
  if (
    t.includes('salon') ||
    t.includes('coiffure') ||
    t.includes('beauty') ||
    t.includes('esthetic') ||
    t.includes('hairstyling')
  ) {
    return BUSINESS_THEMES.salon;
  }

  // 5. Tailoring
  if (t.includes('tailor') || t.includes('couture') || t.includes('couturier')) {
    return BUSINESS_THEMES.tailoring;
  }

  // 6. Car Rental
  if (t.includes('car_rental') || t.includes('car rental') || t.includes('vehicle') || t.includes('fleet') || t.includes('location voiture')) {
    return BUSINESS_THEMES.car_rental;
  }

  // 7. Photography
  if (t.includes('photo') || t.includes('studio') || t.includes('camera')) {
    return BUSINESS_THEMES.photography;
  }

  // 8. Freelancer & Professional Services
  if (t.includes('freelanc') || t.includes('consult') || t.includes('agency') || t.includes('services') || t.includes('professional')) {
    return BUSINESS_THEMES.freelancer;
  }

  // 9. Standard domains
  if (t.includes('hotel') || t.includes('lodge') || t.includes('resort') || t.includes('motel') || t.includes('hospitality'))
    return BUSINESS_THEMES.hotel;
  if (t.includes('pharmacy') || t.includes('dispensary')) return BUSINESS_THEMES.pharmacy;
  if (t.includes('hospital') || t.includes('clinic') || t.includes('medical') || t.includes('health'))
    return BUSINESS_THEMES.hospital;
  if (t.includes('restaurant') || t.includes('food')) return BUSINESS_THEMES.restaurant;
  if (t.includes('cafe') || t.includes('coffee') || t.includes('bakery')) return BUSINESS_THEMES.cafe;
  if (t.includes('bar') || t.includes('club') || t.includes('nightclub') ||
      t.includes('lounge') || t.includes('pub') || t.includes('liquor'))
    return BUSINESS_THEMES.bar;
  if (t.includes('shoe') || t.includes('footwear')) return BUSINESS_THEMES.shoe;
  if (t.includes('clothing') || t.includes('garment') || t.includes('apparel') || t.includes('vetement') || t.includes('vêtement') || t.includes('habille'))
    return BUSINESS_THEMES.clothing;
  if (t.includes('grocery') || t.includes('supermarket') || t.includes('produce'))
    return BUSINESS_THEMES.grocery;
  if (t.includes('hardware') || t.includes('tools') || t.includes('building'))
    return BUSINESS_THEMES.hardware;
  if (t === '6' || t.includes('jewelry') || t.includes('jewel') || t.includes('vault') || t.includes('gem') || t.includes('bijou'))
    return BUSINESS_THEMES.jewelry;
  if (t.includes('retail') || t.includes('shop') || t.includes('boutique') || t.includes('store') || t.includes('general') || t.includes('magasin'))
    return BUSINESS_THEMES.retail;

  return BUSINESS_THEMES.default;
};

/**
 * Returns just the primary hex color for a business type.
 */
export const getBusinessColor = (businessType?: string | number): string =>
  getBusinessTheme(businessType).primary;

/**
 * Adjusts color brightness (darkens = negative, lightens = positive).
 */
export const adjustColorBrightness = (hex: string, amount: number): string => {
  hex = hex.replace(/^\s*#|\s*$/g, '');
  if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');

  let r = Math.min(255, Math.max(0, parseInt(hex.substr(0, 2), 16) + amount));
  let g = Math.min(255, Math.max(0, parseInt(hex.substr(2, 2), 16) + amount));
  let b = Math.min(255, Math.max(0, parseInt(hex.substr(4, 2), 16) + amount));

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
};
